/**
 * List Effects
 * - stackActions.subUpdate from StackEffects
 * @format
 */
/* eslint-disable @typescript-eslint/no-unused-vars */
// import _pickBy from 'lodash/pickBy';
import { Injectable, inject } from '@angular/core';
import { of, EMPTY } from 'rxjs';
import { withLatestFrom, filter, concatMap, mergeMap, catchError, map } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { State } from '../reducers';
import * as listActions from '../actions/lists.actions';
import * as stackActions from '../actions/stacks.actions';
import { getCollabDraftLists } from '@store/reducers/lists.reducers';
import { selectListEntities } from '@store/selectors/lists.selectors';
import { getId, getId as getStackId, selectStackEntities } from '@store/selectors/stacks.selectors';
import { FilterEntityTypes } from '@store/reducers/viewstate.reducers';
import { getUserId } from '@store/selectors/user.selectors';
import { SentryService } from '@app/core/services/sentry.service';
import { sortFeatured, STACK_PRIVACY } from '@app/shared/models/stack.model';
import { ITEM_TYPE } from '@app/shared/models/layout.model';

const DEBUG_LOGS = false;
const PAGE = '[ListStoreEffects]';

@Injectable()
export class ListsEffects {
  private stackActions$ = inject<Actions<stackActions.ActionsUnion>>(Actions);
  private store$ = inject<Store<State>>(Store);
  private sentryService = inject(SentryService);

  /**
   * If an effect does not yield any actions back to the store. Set
   * `dispatch` to false to hint to @ngrx/effects that it should
   * ignore any elements of this effect stream.
   *
   * The `defer` observable accepts an observable factory function
   * that is called when the observable is subscribed to.
   * Wrapping the database open call in `defer` makes
   * effect easier to test.
   */

  /**
   * Do we need to do anything on subscription update?
   */
  subUpdate$ = createEffect(
    () =>
      this.stackActions$.pipe(
        ofType(stackActions.subUpdate.type),
        concatMap((action) =>
          of(action).pipe(
            withLatestFrom(this.store$.select(selectStackEntities)),
            withLatestFrom(this.store$.select(selectListEntities)),
            withLatestFrom(this.store$.select(getUserId))
          )
        ),
        map(([[[{ stack }, state], listState], userId]) => ({ stack, state, listState, userId })),
        filter(({ stack, state, listState, userId }) => stack?.projectId && stack?.stackId?.length > 0),
        mergeMap(({ stack, state, listState, userId }) => {
          const id = getId(stack.projectId, stack.stackId);
          const entity = state[id];
          const updates = [];
          const isPublished = stack.dtePublished || entity?.dtePublished;
          const isMine = stack.userId === userId;

          // if (stack.featured === 0) {
          //   // functionality exists in the reducer to move to recent...
          // } else
          if (stack.featured && stack.featured > 0) {
            // listsWith doesn't matter here, we should add it to any featured
            const listsToModify = Object.values(listState).filter(
              (value) =>
                value?.itemType === ITEM_TYPE.Stacks &&
                value.filters &&
                value.filters?.type === FilterEntityTypes.Featured &&
                (!value.filters.projectId || value.filters.projectId === stack.projectId)
            );

            // // v2 the index change really isn't the interesting data point here.. it's that the sort should be Featured!
            updates.push(
              ...listsToModify.map((list) => ({
                id: list.id,
                changes: {
                  // get the stack entities, sortFeatured, return updates
                  itemIds: [id, ...list.itemIds.filter((d) => d !== id)]
                    .map((d) => state[d])
                    .filter((item) => item?.projectId && item?.stackId)
                    .sort(sortFeatured)
                    .map((item) => getStackId(item.projectId, item.stackId)),
                },
              }))
            );
            DEBUG_LOGS && console.log(`[ListEffects] Update Stack.Featured...`, { updates, listsToModify, stack });
            return of(listActions.updateLists({ updates }));
          } else if (
            Object.keys(stack).length > 2 &&
            !stack.dtePublished &&
            (!entity || (entity && entity.stackId && !entity.dtePublished))
          ) {
            // checking if there's more than just stackId & projectId (Object.keys.length) - if not, likely a delete operation...
            // this might be a draft, add to any drafts lists for that project (we don't have userId here, so mystudio might not update..
            if (stack.isCollaborative) {
              const collabLists = getCollabDraftLists({
                entities: listState,
                projectId: stack.projectId,
                isCollaborative: stack.isCollaborative,
              });

              if (isMine) {
                updates.push(
                  ...collabLists.map((list) => ({
                    id: list.id,
                    changes: { itemIds: [id, ...list.itemIds.filter((itemId) => itemId && itemId !== id)] },
                  }))
                );
              }
              // console.warn({ collabLists, isMine });

              // updates.push(...collabLists.map(list => ({
              //   id: list.id,
              //   changes: { itemIds: [ id, ...list.itemIds.filter((itemId) => itemId && itemId !== id) ] },
              // })));
            } else if (stack.userId === userId) {
              // this is mine, add to draft list
              const listsToAdd = Object.values(listState).filter(
                (value) =>
                  value?.itemType === ITEM_TYPE.Stacks &&
                  value.filters &&
                  value.filters.type === FilterEntityTypes.StackDrafts &&
                  // if there's no projectId it's my studio, otherwise add to project Drafts
                  (!value.filters.projectId || value.filters.projectId === stack.projectId) &&
                  !value.filters.isCollaborative
              );
              updates.push(
                ...listsToAdd.map((list) => ({
                  id: list.id,
                  changes: { itemIds: [id, ...list.itemIds.filter((itemId) => itemId && itemId !== id)] },
                }))
              );
            }
            DEBUG_LOGS && console.log(`[ListEffects] Update My or Collab Draft...`, { updates, stack });
            return of(listActions.updateLists({ updates }));
          } else if (isPublished && stack.privacy) {
            // the privacy of a published stack may have changed
            const privLists = Object.values(listState).filter(
              (value) =>
                value?.itemType === ITEM_TYPE.Stacks &&
                value.filters &&
                value.filters?.type === FilterEntityTypes.Private &&
                (!value.filters.projectId || value.filters.projectId === stack.projectId)
            );
            if (stack.privacy === STACK_PRIVACY.PUBLIC) {
              // remove from private and add to recents (or featured if >0)
              const addToLists = Object.values(listState).filter(
                (value) =>
                  value?.itemType === ITEM_TYPE.Stacks &&
                  value.filters &&
                  value.filters?.type ===
                    (stack?.featured > 0 ? FilterEntityTypes.Featured : FilterEntityTypes.Recent) &&
                  (!value.filters.projectId || value.filters.projectId === stack.projectId)
              );
              updates.push(
                ...privLists.map((list) => ({
                  id: list.id,
                  changes: {
                    itemIds: [...list.itemIds.filter((d) => d !== id)],
                  },
                }))
              );
              updates.push(
                ...addToLists.map((list) => ({
                  id: list.id,
                  changes: {
                    itemIds: [id, ...list.itemIds.filter((d) => d !== id)],
                  },
                }))
              );
            } else {
              // was changed to private or unlisted - remove from everywhere and then add to private
              const nonPrivLists = Object.values(listState).filter(
                (value) =>
                  value?.itemType === ITEM_TYPE.Stacks &&
                  value.filters &&
                  value.filters?.type !== FilterEntityTypes.Private &&
                  (!value.filters.projectId || value.filters.projectId === stack.projectId)
              );
              updates.push(
                ...nonPrivLists.map((list) => ({
                  id: list.id,
                  changes: {
                    itemIds: [...list.itemIds.filter((d) => d !== id)],
                  },
                }))
              );
              updates.push(
                ...privLists.map((list) => ({
                  id: list.id,
                  changes: {
                    itemIds: [id, ...list.itemIds.filter((d) => d !== id)],
                  },
                }))
              );
            }
            DEBUG_LOGS && console.log({ isPublished, privacy: stack.privacy, updates });
            return of(listActions.updateLists({ updates }));
          } else {
            DEBUG_LOGS && console.log(`${PAGE} ignored subUpdate action`, { stack });
          }

          return EMPTY;
        }),
        catchError((error) => {
          this.captureError(error, 'subUpdate caught');
          this.sentryService.captureError(error);
          return EMPTY;
        })
      )
    //  { dispatch: false },
  );

  private captureError(error, msg = ''): void {
    const e = error && Array.isArray(error.errors) && error.errors.length > 0 ? error.errors[0] : error;
    console.warn(`${PAGE} ${msg} captureError`, e);
    this.sentryService.captureError(e);
  }
}
